import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { AUTHOR_PERSONAS, suggestAuthorBot } from '../../services/ai';
import { api } from '../../../convex/_generated/api';
import { useTranslation } from 'react-i18next';
import { getTranslatedGenre } from '../../utils/i18nHelpers';

const DEFAULT_CUSTOM_MENTOR = {
    title: '',
    description: '',
    promptBrief: '',
    emoji: '🧠',
};

function StepThree({ formData, updateFormData }) {
    const { t } = useTranslation();
    const personas = Object.values(AUTHOR_PERSONAS);
    const customMentors = useQuery(api.authorBots.listCustomAuthorBots, {}) || [];
    const createCustomMentor = useMutation(api.authorBots.createCustomAuthorBot);
    const [showCreateCustom, setShowCreateCustom] = useState(false);
    const [isCreatingCustom, setIsCreatingCustom] = useState(false);
    const [customMentor, setCustomMentor] = useState(DEFAULT_CUSTOM_MENTOR);
    const suggestedId = useMemo(
        () => suggestAuthorBot(formData.genre, formData.synopsis),
        [formData.genre, formData.synopsis]
    );

    useEffect(() => {
        if (!formData.mentor && suggestedId) {
            updateFormData({ mentor: suggestedId });
        }
    }, [formData.mentor, suggestedId, updateFormData]);

    const getPersonaName = (persona) =>
        persona ? t(`onboarding.stepThree.personas.${persona.id}.name`, persona.name) : '—';

    const getPersonaDescription = (persona) =>
        persona ? t(`onboarding.stepThree.personas.${persona.id}.description`, persona.description) : '';

    const getCustomMentorLabel = (mentor) => mentor?.title || t('onboarding.stepThree.customMentor.fallbackName');
    const getCustomMentorDescription = (mentor) => mentor?.description || t('onboarding.stepThree.customMentor.fallbackDescription');

    const currentMentorLabel = useMemo(() => {
        if (!formData.mentor) return '—';
        if (formData.mentor.startsWith('custom:')) {
            const custom = customMentors.find((item) => `custom:${item._id}` === formData.mentor);
            return custom ? getCustomMentorLabel(custom) : t('onboarding.stepThree.customMentor.fallbackName');
        }
        return getPersonaName(AUTHOR_PERSONAS[formData.mentor]);
    }, [customMentors, formData.mentor, t]);

    const resetCustomMentor = () => setCustomMentor(DEFAULT_CUSTOM_MENTOR);

    const handleCreateCustomMentor = async () => {
        if (isCreatingCustom || customMentor.promptBrief.trim().length < 20) return;
        setIsCreatingCustom(true);
        try {
            const result = await createCustomMentor({
                title: customMentor.title || undefined,
                description: customMentor.description || undefined,
                promptBrief: customMentor.promptBrief,
                emoji: customMentor.emoji || undefined,
            });
            updateFormData({ mentor: result.personaId });
            setShowCreateCustom(false);
            resetCustomMentor();
        } finally {
            setIsCreatingCustom(false);
        }
    };

    return (
        <div className="step-three">
            <h2 className="step-title">{t("onboarding.stepThree.title")}</h2>
            <p className="step-subtitle">{t("onboarding.stepThree.subtitle")}</p>

            <div className="mentor-grid">
                {personas.map(persona => (
                    <button
                        key={persona.id}
                        type="button"
                        className={`mentor-card ${formData.mentor === persona.id ? 'selected' : ''}`}
                        onClick={() => updateFormData({ mentor: persona.id })}
                    >
                        <div className="mentor-header">
                            <span className="mentor-emoji">{persona.emoji}</span>
                            <span className="mentor-name">{getPersonaName(persona)}</span>
                            {persona.id === suggestedId && (
                                <span className="mentor-suggested">{t("onboarding.stepThree.suggested")}</span>
                            )}
                        </div>
                        <p className="mentor-description">{getPersonaDescription(persona)}</p>
                    </button>
                ))}

                {customMentors.map((mentor) => {
                    const personaId = `custom:${mentor._id}`;
                    return (
                        <button
                            key={mentor._id}
                            type="button"
                            className={`mentor-card ${formData.mentor === personaId ? 'selected' : ''}`}
                            onClick={() => updateFormData({ mentor: personaId })}
                        >
                            <div className="mentor-header">
                                <span className="mentor-emoji">{mentor.emoji}</span>
                                <span className="mentor-name">{getCustomMentorLabel(mentor)}</span>
                            </div>
                            <p className="mentor-description">{getCustomMentorDescription(mentor)}</p>
                        </button>
                    );
                })}

                <button
                    type="button"
                    className={`mentor-card mentor-card-create ${showCreateCustom ? 'selected' : ''}`}
                    onClick={() => setShowCreateCustom((prev) => !prev)}
                >
                    <div className="mentor-header">
                        <span className="mentor-emoji">✨</span>
                        <span className="mentor-name">{t('onboarding.stepThree.customMentor.cta')}</span>
                    </div>
                    <p className="mentor-description">{t('onboarding.stepThree.customMentor.caption')}</p>
                </button>
            </div>

            {showCreateCustom && (
                <div className="summary-card mentor-custom-composer">
                    <h3 className="summary-title">{t('onboarding.stepThree.customMentor.title')}</h3>
                    <div className="form-group">
                        <label className="form-label">{t('onboarding.stepThree.customMentor.fields.name')}</label>
                        <input
                            type="text"
                            className="input"
                            value={customMentor.title}
                            onChange={(e) => setCustomMentor((prev) => ({ ...prev, title: e.target.value }))}
                            placeholder={t('onboarding.stepThree.customMentor.placeholders.name')}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">{t('onboarding.stepThree.customMentor.fields.description')}</label>
                        <input
                            type="text"
                            className="input"
                            value={customMentor.description}
                            onChange={(e) => setCustomMentor((prev) => ({ ...prev, description: e.target.value }))}
                            placeholder={t('onboarding.stepThree.customMentor.placeholders.description')}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">{t('onboarding.stepThree.customMentor.fields.instructions')}</label>
                        <textarea
                            className="input textarea"
                            rows={5}
                            value={customMentor.promptBrief}
                            onChange={(e) => setCustomMentor((prev) => ({ ...prev, promptBrief: e.target.value }))}
                            placeholder={t('onboarding.stepThree.customMentor.placeholders.instructions')}
                        />
                    </div>
                    <div className="mentor-custom-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowCreateCustom(false);
                                resetCustomMentor();
                            }}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isCreatingCustom || customMentor.promptBrief.trim().length < 20}
                            onClick={() => void handleCreateCustomMentor()}
                        >
                            {isCreatingCustom ? t('common.creating', { defaultValue: 'Creating…' }) : t('onboarding.stepThree.customMentor.save')}
                        </button>
                    </div>
                </div>
            )}

            <div className="summary-card">
                <h3 className="summary-title">{t("onboarding.stepThree.projectSummary")}</h3>
                <div className="summary-row">
                    <span className="summary-label">{t("onboarding.stepThree.titleLabel")}</span>
                    <span className="summary-value">{formData.title || '—'}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">{t("onboarding.stepThree.genreLabel")}</span>
                    <span className="summary-value">{formData.genre ? getTranslatedGenre(t, formData.genre) : '—'}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">{t("onboarding.stepThree.mentorLabel")}</span>
                    <span className="summary-value">{currentMentorLabel}</span>
                </div>
            </div>
        </div>
    );
}

export default StepThree;
